import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import styles from "../../DeviceHealth/FrequencyBucketTable.module.css";
import tableStyles from "./DeviceStateCityTable.module.css";
import TablePagination from "../../DeviceHealth/TablePagination";
import { useLatencyContextData } from "../context/Context";
import useApiRows from "../useApiRows";
import { SWITCHES, filterFromSwitches, getStandardInterval, switchesFromFilter } from "../deviceStates";
import { IntervalCell, IntervalHeader, StatusCells, SwitchHeader } from "./DeviceStateCells";
import DeviceStateDepotPopover, { DEVICE_STATE_DEPOT_MODAL_ID } from "./DeviceStateDepotPopover";
import { getDeviceStateDepotTable } from "../LatencyService";

const ROWS_PER_PAGE = 10; // fixed, not user-changeable
const COL_COUNT = 5 + SWITCHES.length;

// bootstrap 4 + 5 attributes so the shared CustomModal opens either way
const MODAL_TRIGGER = {
    "data-toggle": "modal",
    "data-bs-toggle": "modal",
    "data-target": `#${DEVICE_STATE_DEPOT_MODAL_ID}`,
    "data-bs-target": `#${DEVICE_STATE_DEPOT_MODAL_ID}`
};

const displayValue = (val) =>
    val === null || val === undefined || val === "" ? "-" : val;

const toOption = (value) => [{ label: value, value }];

function ChartLoader() {
    return (
        <div className={styles.loadingState}>
            <span className={styles.spinner} />
            <span>Loading...</span>
        </div>
    );
}

/*
 * City -> depot accordion (same pattern as the Layer-wise latency table); clicking a depot
 * opens its vehicles in a popover. The Mains / Ignition / Emergency header switches are part of
 * GlobalFilter (backupBatteryStatus / ignitionOn / emergencyStatus), so every API gets them.
 */
// `cityRows` / `isLoading`: API 1 (city-wise rows), loaded by DeviceStateCard for its download
export default function DeviceStateCityTable({ cityRows, isLoading }) {
    const { GlobalFilter, setGlobalFilters } = useLatencyContextData();
    const [openCity, setOpenCity] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [popoverSelection, setPopoverSelection] = useState(null);

    const switches = switchesFromFilter(GlobalFilter);
    const standard = getStandardInterval(switches);
    const base = { ...GlobalFilter };

    const onToggleSwitch = (key) =>
        setGlobalFilters((prev) => {
            const current = switchesFromFilter(prev);
            return { ...prev, ...filterFromSwitches({ ...current, [key]: !current[key] }) };
        });

    // API 2: depots of the opened city
    const [depotRows, isDepotLoading] = useApiRows(
        getDeviceStateDepotTable,
        { ...base, city: toOption(openCity) },
        !openCity
    );

    // new filters: back to page 1 with every city closed
    useEffect(() => {
        setCurrentPage(1);
        setOpenCity(null);
    }, [GlobalFilter]);

    const totalItems = cityRows.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / ROWS_PER_PAGE));
    const safePage = Math.min(currentPage, totalPages);

    const paginatedRows = useMemo(() => {
        const start = (safePage - 1) * ROWS_PER_PAGE;
        return cityRows.slice(start, start + ROWS_PER_PAGE);
    }, [cityRows, safePage]);

    const toggleCity = (city) => setOpenCity((prev) => (prev === city ? null : city));

    const changePage = (page) => {
        setOpenCity(null);
        setCurrentPage(page);
    };

    const stateRow = (rowClass, content) => (
        <tr className={rowClass}>
            <td colSpan={COL_COUNT}>{content}</td>
        </tr>
    );

    // Total Vehicle · Total Active Fleet · statuses · Actual (today) interval · Actual Count
    const metricCells = (row) => (
        <>
            <td>{displayValue(row.totalVehicles)}</td>
            <td>{displayValue(row.activeFleet)}</td>
            <StatusCells values={switches} />
            <IntervalCell actual={row.actualInterval} standard={standard} />
            <td className={tableStyles.actualCount}>{displayValue(row.actualCount)}</td>
        </>
    );

    /* ---------- depot rows: click opens the vehicle popover ---------- */
    const renderDepotRows = () => {
        if (isDepotLoading) return stateRow(styles.depotRow, <ChartLoader />);
        if (!depotRows.length) {
            return stateRow(styles.depotRow, <div className={styles.loadingState}>No depots found</div>);
        }

        return depotRows.map((row, idx) => (
            <tr
                key={`depot-${row.Depot || idx}`}
                {...MODAL_TRIGGER}
                className={`${styles.depotRow} ${styles.depotRowClickable}`}
                title={`${row.Depot} — click to view vehicles`}
                onClick={() => setPopoverSelection({ city: row.City ?? openCity, depot: row.Depot, switches })}
            >
                <td className={styles.depotCell}>
                    <span className={styles.cityContent}>
                        <span className={styles.depotIndicator} />
                        <button type="button" className={styles.depotLink}>
                            {displayValue(row.Depot)}
                        </button>
                    </span>
                </td>
                {metricCells(row)}
            </tr>
        ));
    };

    /* ---------- city rows ---------- */
    const renderCityRow = (row) => {
        const isOpen = openCity === row.City;

        return (
            <React.Fragment key={row.City}>
                <tr
                    className={`${styles.cityRow} ${isOpen ? styles.cityRowActive : ""}`}
                    onClick={() => toggleCity(row.City)}
                >
                    <td className={styles.cityCell}>
                        <span className={styles.cityContent}>
                            <span className={styles.accordionIcon}>
                                {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                            </span>
                            <span
                                className={styles.cityName}
                                title={`${row.City} — click to ${isOpen ? "hide" : "view"} depots`}
                            >
                                {row.City}
                            </span>
                        </span>
                    </td>
                    {metricCells(row)}
                </tr>

                {isOpen && renderDepotRows()}
            </React.Fragment>
        );
    };

    return (
        <div className={tableStyles.section}>
            <div className={`table-responsive mt-1 ${styles.tableWrapper}`}>
                <table className={`table table-bordered table-hover align-middle mb-0 ${styles.mainTable} ${tableStyles.table}`}>
                    <colgroup>
                        <col className={tableStyles.colLocation} />
                        <col className={tableStyles.colCount} />
                        <col className={tableStyles.colCount} />
                        {SWITCHES.map((s) => (
                            <col key={s.key} className={tableStyles.colStatus} />
                        ))}
                        <col className={tableStyles.colInterval} />
                        <col className={tableStyles.colCount} />
                    </colgroup>

                    <thead className="sticky-top">
                        <tr>
                            <th>City / Depot</th>
                            <th>Total Vehicle</th>
                            <th>Total Active Fleet</th>
                            {SWITCHES.map((s) => (
                                <SwitchHeader
                                    key={s.key}
                                    item={s}
                                    checked={switches[s.key]}
                                    onToggle={() => onToggleSwitch(s.key)}
                                />
                            ))}
                            <IntervalHeader standard={standard} />
                            <th title="Active vehicles currently in this Mains / Ignition / Emergency combination">
                                Actual Count
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {isLoading ? (
                            stateRow("", <ChartLoader />)
                        ) : totalItems === 0 ? (
                            stateRow("", <div className={styles.loadingState}>No cities found</div>)
                        ) : (
                            paginatedRows.map(renderCityRow)
                        )}
                    </tbody>
                </table>
            </div>

            {!isLoading && (
                <TablePagination
                    page={safePage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    pageSize={ROWS_PER_PAGE}
                    onPageChange={changePage}
                    itemLabel="cities"
                />
            )}

            <DeviceStateDepotPopover selection={popoverSelection} />
        </div>
    );
}
